import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-guard-dog-page',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './guard-dog-page.component.html',
  styleUrl: './guard-dog-page.component.scss'
})
export class GuardDogPageComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  nav(url: any) {
    console.log(url);
    this.router.navigateByUrl(url)
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
injectSchemaMarkup(): void {
    const guardDogSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/guard-dog-page",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/guard-dog-page",
          "name": "100% Web-Based Alert-Based Cellular Monitoring System | Kalpita Case Study",
          "description": "Kalpita Technologies built a 100% web-based, ready-to-deploy cellular monitoring system with real-time alerts via phone, email, and text for critical machinery and equipment monitoring.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".guard-head",
              ".guard-para",
              ".heading-guarddog"
            ]
          },
          "datePublished": "2024-01-15",
          "dateModified": "2024-01-15",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/guard-dog-page#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Case Studies", "item": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study" },
            { "@type": "ListItem", "position": 3, "name": "Guard Dog Cellular Monitoring System" }
          ]
        },
        {
          "@type": "Article",
          "headline": "100% Web-Based, Ready to Deploy & Easy-to-Use Alert-Based Cellular Monitoring System",
          "articleBody": "Kalpita Technologies developed a 100% web-based, ready-to-deploy cellular monitoring system... results include monitoring pumps preventing sewage from being dumped into creeks and rivers, telemetry and scoring tools.",
          "keywords": [
            "cellular monitoring system", "web-based monitoring", "SCADA alternative", "real-time alerts", "IoT monitoring", "ASP.NET development", "AWS cloud", "Microsoft Azure", "React frontend"
          ],
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2024-01-15",
          "image": "https://www.kalpitatechnologies.com/assets/resources/casestudies/guard-dogimg.webp",
          "articleSection": "Case Studies"
        },
        {
          "@type": "HowTo",
          "name": "How to Build a Web-Based Cellular Monitoring System",
          "description": "Step-by-step approach Kalpita Technologies used to develop a ready-to-deploy alert-based cellular monitoring system.",
          "step": [
            { "@type": "HowToStep", "name": "Define Requirements", "text": "Identify the need to scale a 100% web-based system so users can manage alarms and analyze trends." },
            { "@type": "HowToStep", "name": "Design for Quick Setup", "text": "Devise a system that can be set up and used within minutes." },
            { "@type": "HowToStep", "name": "Implement Multi-Channel Notifications", "text": "Build alerts via text, call, and email, triggering all three simultaneously for critical alarms." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the Guard Dog cellular monitoring system?",
              "acceptedAnswer": { "@type": "Answer", "text": "It is a 100% web-based cellular monitoring platform that serves as an economical backup to traditional SCADA systems." }
            },
            {
              "@type": "Question",
              "name": "What technologies were used to build this?",
              "acceptedAnswer": { "@type": "Answer", "text": "Built using .NET, ASP.NET, React, and deployed on both AWS and Microsoft Azure." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin", "addressRegion": "TX", "postalCode": "78746", "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(guardDogSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
